<template>
  <ConfigPageShell
    title="Vehicle Field Mappings"
    subtitle="Define where each vehicle field's data comes from. Adjust source priority or add new fields as needed."
    dense
  >
    <template #actions>
      <v-tooltip text="Create a custom field for additional data" location="bottom">
        <template #activator="{ props: tooltipProps }">
          <v-btn
            v-if="activeSegment === 'all'"
            v-bind="tooltipProps"
            color="primary"
            variant="tonal"
            prepend-icon="mdi-plus"
            @click="openAddCustomFieldModal"
          >
            Add Custom Field
          </v-btn>
        </template>
      </v-tooltip>
      <v-btn
        v-if="!mdAndDown"
        color="primary"
        variant="flat"
        @click="handleSaveFieldMapping"
        :loading="isSaving"
        :disabled="isSaving || (props.isOnBoarding ? false : !hasUnsavedChanges)"
      >
        {{ props.isOnBoarding ? 'Next' : 'Save' }}
      </v-btn>
    </template>

    <template #belowHeader>
        <div class="text-caption text-grey-darken-1 mb-6" style="font-style: italic">
          💡 Tip: Start with the defaults — adjust only if a field pulls incorrect or missing data.
        </div>
    </template>

        <v-data-table
          :headers="fieldHeaders"
          :items="filteredFields"
          :items-per-page="-1"
          hide-default-footer
          :loading="isTableLoading"
          :item-class="getRowClass"
          item-value="name"
          class="field-mapping-table"
        >
          <template v-slot:top>
            <v-row>
              <v-col cols="12" md="4">
                <v-select
                  label="Segment to configure"
                  v-model="activeSegment"
                  :items="segmentationOptions"
                  :disabled="isTableLoading"
                  item-title="text"
                  item-value="value"
                />
              </v-col>
              <v-col cols="12" md="8">
                <div class="d-flex align-center">
                  <v-text-field
                    v-model="search"
                    :placeholder="'Search by field name or source…'"
                    prepend-inner-icon="mdi-magnify"
                    variant="outlined"
                    hide-details
                    single-line
                    class="flex-grow-1 mr-4"
                  />
                </div>
              </v-col>
            </v-row>

            <div class="text-caption text-grey-darken-1 mt-1">
              Currently editing: <strong>{{ currentSegmentLabel }}</strong>
              <span v-if="activeSegment === 'all'">(default mappings apply to all segments)</span>
            </div>

            <div class="mt-3 d-flex align-center ga-2 flex-wrap">
              <span class="text-caption text-grey-darken-1">Filter:</span>
              <v-chip
                size="small"
                :variant="filterOptions.showModifiedOnly ? 'flat' : 'outlined'"
                :color="filterOptions.showModifiedOnly ? 'warning' : 'default'"
                @click="filterOptions.showModifiedOnly = !filterOptions.showModifiedOnly"
                class="filter-chip"
              >
                <v-icon start size="small">mdi-pencil</v-icon>
                Changed ({{ changedFieldsForDisplay.size }})
              </v-chip>
              <v-chip
                size="small"
                :variant="filterOptions.showCustomOnly ? 'flat' : 'outlined'"
                :color="filterOptions.showCustomOnly ? 'info' : 'default'"
                @click="filterOptions.showCustomOnly = !filterOptions.showCustomOnly"
                class="filter-chip"
              >
                <v-icon start size="small">mdi-star</v-icon>
                Custom ({{ customFieldCount }})
              </v-chip>
              <v-chip
                v-if="invalidSourceFieldCount > 0"
                size="small"
                :variant="filterOptions.showInvalidSourcesOnly ? 'flat' : 'outlined'"
                :color="'warning'"
                @click="filterOptions.showInvalidSourcesOnly = !filterOptions.showInvalidSourcesOnly"
                class="filter-chip"
              >
                <v-icon start size="small">mdi-alert-circle</v-icon>
                Needs Attention ({{ invalidSourceFieldCount }})
              </v-chip>
              
              <!-- Source filter chips -->
              <v-chip
                v-for="source in availableSourcesForFilter"
                :key="source.id"
                size="small"
                :variant="filterOptions.selectedSources.includes(source.id) ? 'flat' : 'outlined'"
                :color="filterOptions.selectedSources.includes(source.id) ? 'primary' : 'default'"
                @click="toggleSourceFilter(source.id)"
                class="filter-chip"
              >
                <v-icon start size="small">mdi-database</v-icon>
                {{ source.friendlyName || source.id }}
              </v-chip>
              
              <v-btn
                v-if="hasActiveFilters"
                size="x-small"
                variant="text"
                @click="clearFilters"
              >
                Clear filters
              </v-btn>
            </div>

            <div
              class="text-caption text-grey-darken-1 mt-2"
              v-if="filteredFieldCount !== totalFieldCount"
            >
              Showing {{ filteredFieldCount }} of {{ totalFieldCount }} fields
            </div>

            <div
              v-if="bulkSourceSelectItems.length > 0"
              class="field-mapping-bulk-actions mt-3 pa-3 rounded border"
            >
              <div class="text-subtitle-2">Bulk Actions</div>
              <div class="text-caption text-medium-emphasis mb-3">
                Applies to all applicable fields, including fields hidden by filters.
              </div>
              <div class="field-mapping-bulk-actions__ops d-flex flex-wrap align-center">
                <div class="field-mapping-bulk-actions__group d-flex flex-wrap align-center ga-2">
                  <v-select
                    v-model="bulkPrimarySource"
                    :items="bulkSourceSelectItems"
                    item-title="text"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                    label="Primary Source"
                    :disabled="isSaving || isTableLoading"
                    data-testid="field-mapping-bulk-primary-source"
                  />
                  <v-btn
                    variant="outlined"
                    color="primary"
                    :disabled="isSaving || isTableLoading || !bulkPrimarySource"
                    data-testid="field-mapping-bulk-primary-apply"
                    @click="applyBulkPrimarySource"
                  >
                    Apply to all fields
                  </v-btn>
                </div>
                <div class="field-mapping-bulk-actions__group d-flex flex-wrap align-center ga-2">
                  <v-select
                    v-model="bulkFallbackSource"
                    :items="bulkSourceSelectItems"
                    item-title="text"
                    item-value="value"
                    density="compact"
                    variant="outlined"
                    hide-details
                    clearable
                    label="Fallback Source"
                    :disabled="isSaving || isTableLoading"
                    data-testid="field-mapping-bulk-fallback-source"
                  />
                  <v-btn
                    variant="outlined"
                    color="primary"
                    :disabled="isSaving || isTableLoading || !bulkFallbackSource"
                    data-testid="field-mapping-bulk-fallback-apply"
                    @click="applyBulkFallbackSource"
                  >
                    Apply to all fields
                  </v-btn>
                </div>
              </div>
            </div>
          </template>

          <!-- Field Name with Group Rows -->
          <template v-slot:item.name="{ item }">
            <!-- Group header row -->
            <div v-if="(item as any).__group" class="field-group-header">
              {{ (item as any).groupLabel }}
            </div>

            <!-- Regular field row -->
            <div v-else>
              <!-- Custom fields: whole cell clickable with pencil icon -->
              <div
                  v-if="(item as any).custom"
                class="d-flex align-center"
                :class="{ 'custom-field-clickable': activeSegment === 'all' }"
                :role="activeSegment === 'all' ? 'button' : undefined"
                :tabindex="activeSegment === 'all' ? 0 : undefined"
                @click="activeSegment === 'all' && openEditCustomFieldModal(item as any)"
                @keydown.enter.prevent="activeSegment === 'all' && openEditCustomFieldModal(item as any)"
                @keydown.space.prevent="activeSegment === 'all' && openEditCustomFieldModal(item as any)"
              >
                <v-icon
                  size="16"
                  class="mr-2 custom-field-edit-icon"
                >
                  mdi-pencil
                </v-icon>
                <span>{{ (item as any).customLabel || (item as any).name }}</span>
                <v-chip
                  size="x-small"
                  color="info"
                  variant="outlined"
                  class="ml-2"
                >
                  custom
                </v-chip>
                <v-chip
                  v-if="activeSegment !== 'all'"
                  size="x-small"
                  variant="outlined"
                  class="ml-2"
                >
                  Inherited from All Vehicles
                </v-chip>
                <v-chip
                  v-if="isDraftModifiedField(item as any)"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                  class="ml-2"
                >
                  Draft
                </v-chip>
                <v-chip
                  v-else-if="isLocallyModifiedField(item as any)"
                  size="x-small"
                  color="warning"
                  variant="outlined"
                  class="ml-2"
                >
                  Unsaved
                </v-chip>
              </div>
              <!-- Normal fields: read-only code label -->
              <div v-else class="d-flex align-center">
                <code class="field-name-code">{{ (item as any).name }}</code>
                <v-chip
                  v-if="activeSegment !== 'all' && (item as any).origin === 'inherited'"
                  size="x-small"
                  variant="outlined"
                  class="ml-2"
                  data-testid="field-mapping-inherited"
                >
                  Inherited from All Vehicles
                </v-chip>
                <v-btn
                  v-if="activeSegment !== 'all' && (item as any).origin === 'override'"
                  size="x-small"
                  variant="text"
                  class="ml-2"
                  data-testid="field-mapping-reset"
                  @click="resetToAllVehicles(item as any)"
                >
                  Reset to All Vehicles
                </v-btn>
                <v-chip
                  v-if="isDraftModifiedField(item as any)"
                  size="x-small"
                  color="warning"
                  variant="tonal"
                  class="ml-2"
                >
                  Draft
                </v-chip>
                <v-chip
                  v-else-if="isLocallyModifiedField(item as any)"
                  size="x-small"
                  color="warning"
                  variant="outlined"
                  class="ml-2"
                >
                  Unsaved
                </v-chip>
              </div>
            </div>
          </template>

          <!-- Source Selection -->
          <template v-slot:item.source="{ item }">
            <!-- Group rows: keep cell empty -->
            <template v-if="(item as any).__group">
              <span></span>
            </template>
            <!-- Regular field rows -->
            <template v-else>
            <div v-if="(item as any).custom" class="text-body-2">
              {{ getCustomFieldSourceLabel(item as any, 0) }}
            </div>
              <!-- No available sources at all, but mapping has a source that isn't available -->
              <div
                v-else-if="!hasAnySources(item as any) && getCurrentSourceId(item as any)"
                class="text-body-2 text-grey"
              >
                {{ getUnavailableSourceLabel(item as any) }}
              </div>
              <!-- No feed provides this field: row still shows so a default can be set -->
              <div
                v-else-if="!hasAnySources(item as any)"
                class="text-body-2 text-grey"
                data-testid="field-mapping-no-feed-source"
              >
                <div>No feed source available</div>
                <div class="text-caption">Set a default value →</div>
              </div>
              <!-- Sources exist but current mapping points to a provider not available for this account -->
              <!-- CHANGED: Show warning + dropdown instead of blocking the dropdown entirely -->
              <div
                v-else-if="getCurrentSourceId(item as any) && !isCurrentSourceAvailable(item as any)"
                class="d-flex flex-column ga-1"
              >
                <div class="text-caption text-warning">
                  ⚠ {{ getUnavailableSourceLabel(item as any) }}
                </div>
                <!-- Show dropdown with valid options so user can fix the stale selection -->
                <v-select
                  v-model="(item as any).values.source"
                  :items="getSourceSelection(item as any)"
                  item-value="value"
                  item-title="text"
                  density="compact"
                  variant="outlined"
                  hide-details
                  min-width="150"
                  @update:modelValue="(value) => handleChangeSource(item as any, value)"
                />
              </div>
              <!-- A persisted single-source mapping is read-only text. If the field is new
                   and unmapped, show the selector so choosing its sole source stages a change. -->
              <div
                v-else-if="!hasMultipleSources(item as any) && getCurrentSourceId(item as any)"
                class="text-body-2"
              >
                {{ getSingleSourceLabel(item as any) }}
              </div>
              <!-- Otherwise show dropdown to choose among multiple sources -->
            <v-select
              v-else
              v-model="(item as any).values.source"
              :items="getSourceSelection(item as any)"
              item-value="value"
              item-title="text"
              density="compact"
              variant="outlined"
              hide-details
              min-width="150"
              @update:modelValue="(value) => handleChangeSource(item as any, value)"
            ></v-select>
            </template>
          </template>

          <!-- Fallback Source Selection -->
          <template v-slot:item.fallbackSource="{ item }">
            <!-- Group rows: keep cell empty -->
            <template v-if="(item as any).__group">
              <span></span>
            </template>
            <!-- Regular field rows -->
            <template v-else>
            <div v-if="(item as any).custom" class="text-body-2">
              {{ getCustomFieldSourceLabel(item as any, 1) }}
            </div>
              <!-- If there are valid fallback options, ALWAYS show dropdown so user can choose "None" -->
              <div v-else-if="hasAnyFallbackOptions(item as any)" class="d-flex flex-column ga-1">
                <div
                  v-if="getCurrentFallbackId(item as any) && !isCurrentFallbackAvailable(item as any)"
                  class="text-caption text-warning"
                >
                  ⚠ {{ getFallbackUnavailableLabel(item as any) }}
                </div>
                <v-select
                  :model-value="getCurrentFallbackId(item as any) || ''"
                  :items="getFallbackSourceSelectionWithNone(item as any)"
                  item-value="value"
                  item-title="text"
                  density="compact"
                  variant="outlined"
                  hide-details
                  min-width="150"
                  @update:modelValue="(value) => handleChangeFallbackSource(item as any, value)"
                />
              </div>
              <!-- No fallback options, but mapping has a fallback that isn't available -->
              <div
                v-else-if="getCurrentFallbackId(item as any)"
                class="text-body-2 text-grey"
              >
                {{ getFallbackUnavailableLabel(item as any) }}
              </div>
              <!-- No fallback configured -->
              <div v-else class="text-body-2 text-grey">—</div>
          </template>
          </template>

          <!-- Second Fallback Source Selection -->
          <template v-slot:item.secondFallbackSource="{ item }">
            <template v-if="(item as any).__group">
              <span></span>
            </template>
            <template v-else>
              <div v-if="(item as any).custom" class="text-body-2">
                {{ getCustomFieldSourceLabel(item as any, 2) }}
              </div>
              <div
                v-else-if="hasAnyFallbackOptions(item as any, 1)"
                class="d-flex flex-column ga-1"
              >
                <div
                  v-if="
                    getCurrentFallbackId(item as any, 1) &&
                    !isCurrentFallbackAvailable(item as any, 1)
                  "
                  class="text-caption text-warning"
                >
                  ⚠ {{ getFallbackUnavailableLabel(item as any, 1) }}
                </div>
                <v-select
                  :model-value="getCurrentFallbackId(item as any, 1) || ''"
                  :items="getFallbackSourceSelectionWithNone(item as any, 1)"
                  item-value="value"
                  item-title="text"
                  density="compact"
                  variant="outlined"
                  hide-details
                  min-width="150"
                  @update:modelValue="
                    (value) => handleChangeFallbackSource(item as any, value, 1)
                  "
                />
              </div>
              <div
                v-else-if="getCurrentFallbackId(item as any, 1)"
                class="text-body-2 text-grey"
              >
                {{ getFallbackUnavailableLabel(item as any, 1) }}
              </div>
              <div v-else class="text-body-2 text-grey">—</div>
              <div
                v-if="Array.isArray((item as any).values?.fallback) && (item as any).values.fallback.length > 2"
                class="text-caption text-warning mt-1"
              >
                {{ (item as any).values.fallback.length - 2 }} additional source(s) preserved
              </div>
            </template>
          </template>

          <!-- Default Value (optional) -->
          <template v-slot:item.defaultValue="{ item }">
            <!-- Group rows: keep cell empty -->
            <template v-if="(item as any).__group">
              <span></span>
            </template>

            <!-- Regular field rows -->
            <template v-else>
              <v-text-field
                v-model="(item as any).policy.default_value"
                density="compact"
                variant="outlined"
                hide-details
                :data-testid="`field-mapping-default-${(item as any).name}`"
                :placeholder="getDefaultValuePlaceholder(item as any)"
                @update:modelValue="(v) => handleChangeDefaultValue(item as any, v)"
              />
            </template>
          </template>

        </v-data-table>

        <div v-if="activeSegment !== 'all'">
          <!-- if there *are* fields to add, show the selector+button -->
          <template v-if="addableFieldsForThisSegment.length > 0">
            <div style="display: flex; justify-content: space-between; align-items: center">
              <div style="flex-grow: 1"></div>
              <v-select
                v-model="fieldToAdd as any"
                :items="addableFieldsForThisSegment"
                placeholder="Select a field"
                item-value="value"
                item-title="text"
                density="compact"
                variant="outlined"
                style="max-width: 200px; margin-right: 10px"
                hide-details
              />
              <v-btn
                :disabled="!fieldToAdd"
                prepend-icon="mdi-plus"
                variant="flat"
                color="primary"
                @click="handleAddField"
              >
                Add Field
              </v-btn>
              <div style="flex-grow: 1"></div>
            </div>
          </template>

          <!-- otherwise show a no-fields-available message -->
          <template v-else>
            <div class="text-center pa-4 grey--text">
              There are no fields with an alternate source to add.
            </div>
          </template>
        </div>
      <!-- Custom Field Modal -->
      <CustomFieldModal
        v-model="showCustomFieldModal"
        :edit-data="editingCustomField"
        :existing-field-ids="existingCustomFieldIds"
        :normalized-field-ids="normalizedFieldIds"
        :available-providers="availableSources"
        @save="handleSaveCustomField"
        @delete="handleDeleteCustomFieldFromModal"
      />

      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="showDeleteDialog" max-width="500px">
        <v-card>
          <v-card-title class="text-h6">Delete Custom Field</v-card-title>
          <v-card-text>
            Delete custom field '<strong>{{ deletingField?.customLabel }}</strong
            >'? <br /><br />
            This removes it from this segment's mapping. This does not affect previously indexed
            documents.
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn variant="text" @click="showDeleteDialog = false">Cancel</v-btn>
            <v-btn color="error" variant="flat" @click="handleDeleteCustomField">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </ConfigPageShell>

    <!-- Floating Save button (mobile only) -->
    <v-fab-transition>
      <v-btn
        v-if="mdAndDown && (props.isOnBoarding || hasUnsavedChanges)"
        class="save-fab"
        color="primary"
        variant="elevated"
        size="large"
        :loading="isSaving"
        :disabled="isSaving"
        @click="handleSaveFieldMapping"
      >
        <v-icon start>mdi-content-save</v-icon>
        Save
      </v-btn>
    </v-fab-transition>
</template>

<script lang="ts" setup>
import ConfigPageShell from '@/components/config/ConfigPageShell.vue'
import { useAccountSettingsStore } from '@/stores/accountSettings'
import { useSourcesStore } from '@/stores/sourcesStore'
import { useJazelAppConfig } from '@jazel/jazel-vuetify'
import { makeAlert } from '@/utils'
import { computed, onMounted, ref, watch } from 'vue'
import { useSegmentStore } from '@/stores/segment'
import { usePublishStore } from '@/stores/usePublishStore'
import { fetchIseMappingFields } from '@/services/fieldMappingService'
import { fetchFieldMappingDraft } from '@/services/configDraftService'
import { fetchSegmentationConfig } from '@/services/segmentationConfigService'
import {
  applyFieldMappingDraftOverlay,
  collectDraftFieldMappingFieldsForSegment,
  type FieldMappingDraftPatchesV3,
} from '@/utils/fieldMappingDraftOverlay'
import {
  buildRecommendedCatalogMappings,
  collectLegacyDerivedFeatureSelections,
  DERIVED_FEATURE_FIELD_NAMES,
  ensureCatalogFieldsPresent,
  isDerivedFeatureFieldName,
  type FieldMappingCatalogField,
} from '@/utils/fieldMappingCatalog'
import {
  collectNormalizedCatalogSourceIds,
  compareFieldMappingLocalDirty,
  isBulkFallbackApplicable,
  isBulkPrimaryApplicable,
} from '@/utils/fieldMappingSourceSelection'
import CustomFieldModal, { type CustomFieldData } from '@/components/CustomFieldModal.vue'
import { useDisplay } from 'vuetify'
import type { SegmentFieldMapping } from '@/types/appTypes'
import {
  buildEffectiveSegmentRows,
  buildSparseSegmentDefaultValues,
  buildSparseSegmentSelections,
  isFieldMappingSegment,
  resetSegmentRowToAll,
  type FieldMappingSegment,
} from '@/utils/segmentFieldMapping'

const props = defineProps<{
  segment?: string
  isOnBoarding?: boolean
}>()

const emit = defineEmits<{
  (e: 'done'): void
}>()

interface TableHeader {
  width?: string
  title: string
  key: string
  sortable?: boolean
  align?: 'start' | 'end' | 'center'
  fixed?: boolean
  children?: Array<TableHeader>
}

const fieldHeaders: TableHeader[] = [
  { title: 'Fields', key: 'name', sortable: false, align: 'start', fixed: false },
  { title: 'Source', key: 'source', sortable: false, align: 'start', fixed: false },
  {
    title: 'Fallback Source',
    key: 'fallbackSource',
    sortable: false,
    align: 'start',
    fixed: false,
  },
  {
    title: 'Second Fallback',
    key: 'secondFallbackSource',
    sortable: false,
    align: 'start',
    fixed: false,
  },
  { title: 'Default Value', key: 'defaultValue', sortable: false, align: 'start', fixed: false },
]

const accountSettings = useAccountSettingsStore()
const publishStore = usePublishStore()
const sourcesStore = useSourcesStore()
const { mdAndDown, smAndDown } = useDisplay()

const fieldToAdd = ref<string | null>(null)
const segmentStore = useSegmentStore()
const appConfig = useJazelAppConfig()
const search = ref('')
const segmentationOptions = ref<any[]>([])
const availableSources = computed(() => sourcesStore.sources)
const isSaving = ref(false)
const bulkPrimarySource = ref<string | null>(null)
const bulkFallbackSource = ref<string | null>(null)

const currentSegmentLabel = computed(() => {
  const opt = segmentationOptions.value.find(
    (o: any) => String(o.value) === String(activeSegment.value),
  )
  return opt?.text || 'All Vehicles'
})

const totalFieldCount = computed(() => {
  // Total across current segment list; since we already load only the active segment into segmentConfig
  // use that array length as the total baseline for the counter (pre-filter count)
  const fields = segmentConfig.value[activeSegment.value] || []
  return fields.length
})

const customFieldCount = computed(() => {
  return sortedFields.value.filter((f: any) => f.custom).length
})

// Count fields with invalid/missing sources
const invalidSourceFieldCount = computed(() => {
  const fields = segmentConfig.value[activeSegment.value] || []
  return fields.filter((f: any) => {
    // Skip custom fields (they have different validation)
    if (f.custom) return false
    
    // Check if field has no available sources
    if (!f.sources || f.sources.length === 0) {
      return true
    }
    
    // Check if current source is not in available sources
    const currentSource = f.values?.source
    if (currentSource && !f.sources.includes(currentSource)) {
      return true
    }
    
    return false
  }).length
})

// Count of filtered fields (excluding group header rows)
const filteredFieldCount = computed(() => {
  return filteredFields.value.filter((f: any) => !f.__group).length
})

// Dirty state tracking
const locallyChangedFieldNames = computed(() => {
  const segmentId = activeSegment.value || 'all'
  const currentFields = (segmentConfig.value[segmentId] || []).filter(
    (field: any) => field && !field.__group && field.name,
  )
  const originalFields = (originalSegmentConfig.value[segmentId] || []).filter(
    (field: any) => field && !field.__group && field.name,
  )
  const currentByName = new Map<string, any>(
    currentFields.map((field: any) => [field.name, field]),
  )
  const originalByName = new Map<string, any>(
    originalFields.map((field: any) => [field.name, field]),
  )
  const changed = new Set<string>()

  for (const field of currentFields) {
    const original = originalByName.get(field.name)
    if (!original || compareFieldMappingLocalDirty(field, original) === 'dirty') {
      changed.add(field.name)
    }
  }

  for (const field of originalFields) {
    if (!currentByName.has(field.name)) changed.add(field.name)
  }

  return changed
})

const hasUnsavedChanges = computed(() => {
  return modifiedFields.value.size > 0 || locallyChangedFieldNames.value.size > 0
})

const changedFieldsForDisplay = computed(() => {
  return new Set([
    ...draftModifiedFields.value,
    ...modifiedFields.value,
    ...locallyChangedFieldNames.value,
  ])
})

const isDraftModifiedField = (item: any): boolean => {
  const raw = (item && (item.raw ?? item)) as any
  return !!raw?.name && draftModifiedFields.value.has(raw.name)
}

const isLocallyModifiedField = (item: any): boolean => {
  const raw = (item && (item.raw ?? item)) as any
  return (
    !!raw?.name &&
    (modifiedFields.value.has(raw.name) || locallyChangedFieldNames.value.has(raw.name))
  )
}

// Row styling based on field type and modification state
const getRowClass = (item: any) => {
  // Vuetify sometimes wraps the original item under `raw`
  const raw = (item && (item.raw ?? item)) as any
  const classes: string[] = []

  if (raw.__group) classes.push('group-header-row')
  if (raw.custom) classes.push('custom-field-row')
  if (draftModifiedFields.value.has(raw.name)) classes.push('draft-field-row')
  if (modifiedFields.value.has(raw.name)) classes.push('modified-field-row')

  return classes.join(' ')
}

// Custom field modal state
const showCustomFieldModal = ref(false)
const editingCustomField = ref<CustomFieldData | null>(null)
const showDeleteDialog = ref(false)
const deletingField = ref<any>(null)

// Computed properties for custom field validation
const existingCustomFieldIds = computed(() => {
  const allFields = segmentConfig.value[activeSegment.value] || []
  return allFields.filter((f: any) => f.custom).map((f: any) => f.name)
})

const normalizedFieldIds = computed(() => {
  const allFields = segmentConfig.value[activeSegment.value] || []
  return Array.from(
    new Set([
      ...allFields.filter((f: any) => !f.custom).map((f: any) => f.name),
      ...DERIVED_FEATURE_FIELD_NAMES,
    ]),
  )
})

// Sort fields by priority groups, then alphabetically
const sortedFields = computed(() => {
  const allFields = segmentConfig.value[activeSegment.value] || []

  // Separate normalized and custom fields
  const normalizedFields = allFields.filter((f: any) => !f.custom)
  const customFields = allFields.filter((f: any) => f.custom)

  // Define priority groups (fields that should appear first)
  const priorityGroups = [
    // Status & Basic Info
    ['Status', 'condition', 'state'],
    
    // Vehicle Identity (YMMT)
    ['year', 'make', 'model', 'trim', 'vin', 'stock_number'],
    
    // Body & Style
    ['body_type', 'cab_style', 'doors'],
    
    // Pricing
    ['price', 'display_price', 'msrp', 'discount', 'kbb_price', 'incentive_amount'],
    
    // Colors
    ['exterior_color', 'exterior_factory_color', 'interior_color', 'interior_factory_color'],
    
    // EV & Fuel Economy
    ['mpg_combined', 'mpg_city_average', 'mpg_city_high', 'mpg_city_low', 
     'mpg_highway_average', 'mpg_highway_high', 'mpg_highway_low'],
    
    // Mileage & Age
    ['mileage', 'date_in_stock', 'date_in_stock_ts', 'modified_date'],
    
    // Drivetrain & Mechanical
    ['drivetrain', 'drivetrain_code', 'engine', 'engine_displacement', 'engine_cylinders',
     'engine_horsepower', 'engine_torque', 'engine_fuel_tank_capacity', 'transmission', 'transmission_description'],
    
    // Fuel Types
    ['fuel_type', 'fuel_types'],
    
    // Seating & Capacity
    ['passenger_capacity', 'passenger_capacity_text'],
    
    // Features & Equipment
    ['features', 'generic_equipment', 'entertainment_features', 'exterior_features', 
     'interior_features', 'mechanical_features', 'safety_features', 'packages', 'options_json', 'tech_specs'],
  ]

  // Flatten priority list and create a map for quick lookup
  const priorityMap = new Map<string, number>()
  priorityGroups.forEach((group, groupIndex) => {
    group.forEach((fieldName, fieldIndex) => {
      priorityMap.set(fieldName.toLowerCase(), groupIndex * 1000 + fieldIndex)
    })
  })

  // Sort normalized fields by priority, then alphabetically
  const sortedNormalized = normalizedFields.sort((a: any, b: any) => {
    const nameA = (a.name || '').toLowerCase()
    const nameB = (b.name || '').toLowerCase()
    
    const priorityA = priorityMap.get(nameA) ?? 999999
    const priorityB = priorityMap.get(nameB) ?? 999999
    
    // If priorities are different, sort by priority
    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }
    
    // If same priority (or both not in priority list), sort alphabetically
    return nameA.localeCompare(nameB)
  })

  // Sort custom fields alphabetically by label
  const sortedCustom = customFields.sort((a: any, b: any) => {
    const nameA = (a.customLabel || a.name || '').toLowerCase()
    const nameB = (b.customLabel || b.name || '').toLowerCase()
    return nameA.localeCompare(nameB)
  })

  // Return prioritized normalized fields first, then custom fields
  return [...sortedNormalized, ...sortedCustom]
})

// Client-side instant filter based on search text (matches name or source label)
const filteredFields = computed(() => {
  let fields = sortedFields.value

  // Apply type filters first
  if (filterOptions.value.showModifiedOnly) {
    fields = fields.filter((f: any) => changedFieldsForDisplay.value.has(f.name))
  }

  if (filterOptions.value.showCustomOnly) {
    fields = fields.filter((f: any) => f.custom)
  }

  if (filterOptions.value.showInvalidSourcesOnly) {
    fields = fields.filter((f: any) => {
      // Skip custom fields
      if (f.custom) return false
      
      // Include if field has no available sources
      if (!f.sources || f.sources.length === 0) {
        return true
      }
      
      // Include if current source is not in available sources
      const currentSource = f.values?.source
      if (currentSource && !f.sources.includes(currentSource)) {
        return true
      }
      
      return false
    })
  }

  // Apply source filter
  if (filterOptions.value.selectedSources.length > 0) {
    fields = fields.filter((f: any) => {
      // For custom fields, check customSources
      if (f.custom && f.customSources) {
        return f.customSources.some((s: any) => 
          filterOptions.value.selectedSources.includes(s.id)
        )
      }
      
      // For normalized fields, check sources array
      if (f.sources && Array.isArray(f.sources)) {
        return f.sources.some((source: any) => {
          const sourceId = typeof source === 'string' ? source : source.id
          return filterOptions.value.selectedSources.includes(sourceId)
        })
      }
      
      return false
    })
  }

  // Apply search filter
  const q = (search.value || '').toLowerCase().trim()
  if (q) {
    fields = fields.filter((f: any) => {
      const name = String(f.customLabel || f.name || '').toLowerCase()
      const srcLabel = String(
        getCustomFieldSourceLabel(f as any, 0) || f.values?.source || '',
      ).toLowerCase()
      return name.includes(q) || srcLabel.includes(q)
    })
  }

  // Inject group rows before first field of each group
  const rows: any[] = []
  let currentGroup: string | null = null

  fields.forEach((field: any) => {
    const groupLabel = getFieldGroupLabel(field.name)
    if (groupLabel && groupLabel !== currentGroup) {
      rows.push({
        __group: true,
        groupLabel,
        name: `__group_${groupLabel}`,
      })
      currentGroup = groupLabel
    }
    rows.push(field)
  })

  return rows
})

// Group label mapping for fields (used for UI headers)
const fieldGroupLabels: Record<string, string> = {
  status: 'Status',
  condition: 'Status',
  state: 'Status',

  year: 'Core Identity',
  make: 'Core Identity',
  model: 'Core Identity',
  trim: 'Core Identity',
  vin: 'Core Identity',
  stock_number: 'Core Identity',

  body_type: 'Body & Style',
  cab_style: 'Body & Style',
  doors: 'Body & Style',

  price: 'Pricing',
  display_price: 'Pricing',
  msrp: 'Pricing',
  discount: 'Pricing',
  kbb_price: 'Pricing',
  incentive_amount: 'Pricing',

  exterior_color: 'Colors',
  exterior_factory_color: 'Colors',
  interior_color: 'Colors',
  interior_factory_color: 'Colors',

  mpg_combined: 'EV & MPG',
  mpg_city_average: 'EV & MPG',
  mpg_city_high: 'EV & MPG',
  mpg_city_low: 'EV & MPG',
  mpg_highway_average: 'EV & MPG',
  mpg_highway_high: 'EV & MPG',
  mpg_highway_low: 'EV & MPG',

  mileage: 'Mileage & Age',
  date_in_stock: 'Mileage & Age',
  date_in_stock_ts: 'Mileage & Age',
  modified_date: 'Mileage & Age',

  drivetrain: 'Drivetrain & Mechanical',
  drivetrain_code: 'Drivetrain & Mechanical',
  engine: 'Drivetrain & Mechanical',
  engine_displacement: 'Drivetrain & Mechanical',
  engine_cylinders: 'Drivetrain & Mechanical',
  engine_horsepower: 'Drivetrain & Mechanical',
  engine_torque: 'Drivetrain & Mechanical',
  engine_fuel_tank_capacity: 'Drivetrain & Mechanical',
  transmission: 'Drivetrain & Mechanical',
  transmission_description: 'Drivetrain & Mechanical',

  fuel_type: 'Fuel Types',
  fuel_types: 'Fuel Types',

  passenger_capacity: 'Seating & Capacity',
  passenger_capacity_text: 'Seating & Capacity',

  features: 'Features & Equipment',
  generic_equipment: 'Features & Equipment',
  entertainment_features: 'Features & Equipment',
  exterior_features: 'Features & Equipment',
  interior_features: 'Features & Equipment',
  mechanical_features: 'Features & Equipment',
  safety_features: 'Features & Equipment',
  packages: 'Features & Equipment',
  options_json: 'Features & Equipment',
  tech_specs: 'Features & Equipment',
}

const getFieldGroupLabel = (fieldName: string): string | null => {
  const key = String(fieldName || '').toLowerCase()
  return fieldGroupLabels[key] || null
}


// Computed property for available sources to show as filter chips
// Extract unique sources from the actual fields in the current segment
const availableSourcesForFilter = computed(() => {
  const fields = segmentConfig.value[activeSegment.value] || []
  const sourceSet = new Set<string>()
  
  fields.forEach((field: any) => {
    // For custom fields, get sources from customSources
    if (field.custom && field.customSources) {
      field.customSources.forEach((s: any) => {
        sourceSet.add(s.id)
      })
    }
    // For normalized fields, get sources from sources array
    else if (field.sources && Array.isArray(field.sources)) {
      field.sources.forEach((source: any) => {
        const sourceId = typeof source === 'string' ? source : source.id
        if (sourceId) {
          sourceSet.add(sourceId)
        }
      })
    }
  })
  
  // Convert to array and map to objects with friendly names
  return Array.from(sourceSet).map(sourceId => {
    const sourceInfo = availableSources.value.find(s => s.id === sourceId)
    return {
      id: sourceId,
      friendlyName: sourceInfo?.friendlyName || sourceId
    }
  }).sort((a, b) => a.friendlyName.localeCompare(b.friendlyName))
})

const bulkSourceSelectItems = computed(() => {
  const ids = collectNormalizedCatalogSourceIds(segmentConfig.value['all'] || [])
  return ids
    .map((sourceId) => {
      const sourceInfo = availableSources.value.find((s) => s.id === sourceId)
      return {
        text: sourceInfo?.friendlyName || sourceId,
        value: sourceId,
      }
    })
    .sort((a, b) => a.text.localeCompare(b.text))
})

// Check if any filters are active
const hasActiveFilters = computed(() => {
  return filterOptions.value.showModifiedOnly || 
         filterOptions.value.showCustomOnly || 
         filterOptions.value.showInvalidSourcesOnly || 
         filterOptions.value.selectedSources.length > 0
})

// Toggle source filter
const toggleSourceFilter = (sourceId: string) => {
  const index = filterOptions.value.selectedSources.indexOf(sourceId)
  if (index > -1) {
    filterOptions.value.selectedSources.splice(index, 1)
  } else {
    filterOptions.value.selectedSources.push(sourceId)
  }
}

// Clear all filters
const clearFilters = () => {
  filterOptions.value.showModifiedOnly = false
  filterOptions.value.showCustomOnly = false
  filterOptions.value.showInvalidSourcesOnly = false
  filterOptions.value.selectedSources = []
}

// Helpers for source rendering
const hasMultipleSources = (item: any): boolean => {
  const sources = item?.sources && Array.isArray(item.sources) ? item.sources : []
  return sources.length > 1
}

const hasAnySources = (item: any): boolean => {
  const sources = item?.sources && Array.isArray(item.sources) ? item.sources : []
  return sources.length > 0
}

const getCurrentSourceId = (item: any): string => {
  return (item?.values && item.values.source) || ''
}

const isCurrentSourceAvailable = (item: any): boolean => {
  const selection = getSourceSelection(item)
  const current = getCurrentSourceId(item)
  if (!current) return true
  return selection.some((opt: any) => opt.value === current)
}

const getUnavailableSourceLabel = (item: any): string => {
  const current = getCurrentSourceId(item)
  return current ? `${current} not available` : 'Not available'
}

const getSingleSourceLabel = (item: any): string => {
  const selection = getSourceSelection(item)
  const current = (item.values && item.values.source) || ''
  if (current) {
    const match = selection.find((opt: any) => opt.value === current)
    if (match) return match.text
  }
  return selection[0]?.text || current || ''
}

const hasMultipleFallbackSources = (item: any): boolean => {
  const options = getFallbackSourceSelection(item)
  return options.length > 1
}

const getSingleFallbackLabel = (item: any): string => {
  const options = getFallbackSourceSelection(item)
  const current = item.values?.fallback?.[0]?.source || ''
  if (current) {
    const match = options.find((opt: any) => opt.value === current)
    if (match) return match.text
  }
  return options[0]?.text || current || ''
}

const getCurrentFallbackId = (item: any, index = 0): string => {
  return item.values?.fallback?.[index]?.source || ''
}

const hasAnyFallbackOptions = (item: any, index = 0): boolean => {
  const options = getFallbackSourceSelection(item, index)
  return options.length > 0
}

const isCurrentFallbackAvailable = (item: any, index = 0): boolean => {
  const options = getFallbackSourceSelection(item, index)
  const current = getCurrentFallbackId(item, index)
  if (!current) return true
  return options.some((opt: any) => opt.value === current)
}

const getFallbackUnavailableLabel = (item: any, index = 0): string => {
  const current = getCurrentFallbackId(item, index)
  return current ? `${current} not available` : 'Not available'
}

const getSourceSelection = (item: any) => {
  // Use the per-field sources directly from the API response
  if (item.sources && Array.isArray(item.sources)) {
    return item.sources.map((source: any) => {
      // Sources now include import_ prefix from backend
      const rawId = typeof source === 'string' ? source : (source.id || source)
      const sourceId = rawId
      const provider = availableSources.value.find((s) => s.id === sourceId)

      return {
        text: provider?.friendlyName || sourceId,
        value: sourceId,
      }
    })
  }

  return []
}

const getFallbackSourceSelection = (item: any, index = 0) => {
  const selectedSource = item.values?.source || ''
  const selectedFallbacks = (item.values?.fallback || [])
    .map((fallback: any, fallbackIndex: number) =>
      fallbackIndex === index ? '' : fallback?.source || '',
    )
    .filter(Boolean)

  // Use the per-field sources directly from the API response, excluding the selected primary
  if (item.sources && Array.isArray(item.sources)) {
    return item.sources
      .map((source: any) => {
        // Handle both string sources and object sources
        const rawId = typeof source === 'string' ? source : (source.id || source)
        const sourceId = rawId
        return sourceId
      })
      .filter(
        (sourceId: string) =>
          sourceId !== selectedSource && !selectedFallbacks.includes(sourceId),
      )
      .map((sourceId: string) => ({
        text: availableSources.value.find((s) => s.id === sourceId)?.friendlyName || sourceId,
        value: sourceId,
      }))
  }

  return []
}

const getFallbackSourceSelectionWithNone = (item: any, index = 0) => {
  return [
    { text: '— None —', value: '' },
    ...getFallbackSourceSelection(item, index),
  ]
}

const addableFieldsForThisSegment = computed(() => {
  if (Object.keys(segmentConfig.value).length === 0 || !segmentConfig.value[activeSegment.value]) {
    return []
  }

  // For now, return empty array since we're managing fields differently
  // in the new structure. Users can't add/remove fields per segment yet.
  return []
})

const handleRemoveField = (index: number) => {
  // remove from the currently active segment
  if (segmentConfig.value[activeSegment.value]) {
    segmentConfig.value[activeSegment.value].splice(index, 1)
    // Don't mark dirty here - only mark dirty after Save is clicked
  }
}

const handleAddField = () => {
  // Placeholder for future implementation
  // In the new structure, fields are managed globally
  console.warn('Add field not yet implemented in new structure')
}

// Custom field handlers
const openAddCustomFieldModal = () => {
  if (activeSegment.value !== 'all') return
  editingCustomField.value = null
  showCustomFieldModal.value = true
}

const openEditCustomFieldModal = (item: any) => {
  if (activeSegment.value !== 'all') return
  // Convert UI item back to CustomFieldData format
  editingCustomField.value = {
    field: item.name,
    custom: true,
    label: item.customLabel,
    type: item.customType || 'string',
    sources: item.customSources || [],
    required: item.required || false,
    policy: item.policy || { on_missing: 'fallback' },
    default: item.customDefault || null,
    indexing_hint: item.indexing_hint || { searchable: false, facetable: false, sortable: false },
  }
  showCustomFieldModal.value = true
}

const confirmDeleteCustomField = (item: any) => {
  deletingField.value = item
  showDeleteDialog.value = true
}

const handleDeleteCustomField = () => {
  if (!deletingField.value) return

  const fields = segmentConfig.value[activeSegment.value] || []
  const index = fields.findIndex((f: any) => f.name === deletingField.value.name)

  if (index !== -1) {
    const fieldName = deletingField.value.name
    fields.splice(index, 1)
    // Mark this field as modified to enable the Save button
    modifiedFields.value.add(fieldName)
    makeAlert('Custom field deleted', 'success')
  }

  showDeleteDialog.value = false
  deletingField.value = null
}

const handleDeleteCustomFieldFromModal = () => {
  if (!editingCustomField.value) return

  const fields = segmentConfig.value[activeSegment.value] || []
  const item =
    fields.find((f: any) => f.name === editingCustomField.value?.field) || {
      name: editingCustomField.value.field,
    }

  // Use the existing confirmation dialog flow
  deletingField.value = item
  showDeleteDialog.value = true

  // Close the edit modal; dialog will handle final delete
  showCustomFieldModal.value = false
}

const handleSaveCustomField = (customFieldData: CustomFieldData) => {
  // Convert CustomFieldData to UI format
  const uiField = {
    name: customFieldData.field,
    custom: true,
    customLabel: customFieldData.label,
    customType: customFieldData.type,
    // Store sources using feed keys (e.g. "vauto") but keep friendly names for display
    customSources: customFieldData.sources,
    customDefault: customFieldData.default,
    // Persist sources as feed keys (e.g. "vauto"), UI resolves friendly names via availableSources
    sources: customFieldData.sources.map((s) => ({
      id: s.id,
      field: s.field,
      path: s.path || null,
    })),
    values: {
      source: customFieldData.sources[0]?.id || '',
      fallback: customFieldData.sources.slice(1).map((s) => ({ source: s.id })),
    },
    required: customFieldData.required,
    policy: customFieldData.policy,
    indexing_hint: customFieldData.indexing_hint,
  }

  const fields = segmentConfig.value[activeSegment.value] || []

  // Check if editing existing field
  const existingIndex = fields.findIndex((f: any) => f.name === customFieldData.field)

  if (existingIndex !== -1) {
    // Update existing
    fields[existingIndex] = uiField
    makeAlert('Custom field updated', 'success')
  } else {
    // Add new
    fields.push(uiField)
    makeAlert('Custom field added', 'success')
  }

  // Mark this field as modified to enable the Save button
  modifiedFields.value.add(customFieldData.field)
}

const getCustomFieldSourceLabel = (item: any, index: number): string => {
  if (!item.customSources || !item.customSources[index]) return '—'

  const source = item.customSources[index]
  const provider = availableSources.value.find((s) => s.id === source.id)
  const providerLabel = provider?.friendlyName || source.id

  return providerLabel
}

const parseDefaultValueForApi = (raw: unknown): unknown => {
  const s = String(raw ?? '').trim()
  if (!s) return undefined
  try {
    return JSON.parse(s)
  } catch {
    return s
  }
}

const formatDefaultValueForInput = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  if (typeof v === 'string') return v
  try {
    return JSON.stringify(v)
  } catch {
    return String(v)
  }
}

// Suggest the starter default (e.g. state -> "Ready for Sale") as the input hint when the
// catalog provides one; otherwise fall back to a generic example.
const getDefaultValuePlaceholder = (item: any): string => {
  const starter = item?.starterDefaultValue
  if (starter !== undefined && starter !== null && String(starter).trim().length > 0) {
    return `e.g. ${formatDefaultValueForInput(starter)}`
  }
  return 'e.g. 0, true, [], "text"'
}

const handleChangeDefaultValue = (item: any, value: unknown) => {
  if (!item.policy) item.policy = { on_missing: 'fallback' }
  item.policy.default_value = value
  markSegmentRowOrigin(item)

  const originalFields = originalSegmentConfig.value[activeSegment.value] || []
  const originalField = originalFields.find((f: any) => f.name === item.name)
  const current = String(value ?? '').trim()
  const original = String(originalField?.policy?.default_value ?? '').trim()

  // Mark dirty only if it differs from original (or if field did not exist before)
  if (!originalField) {
    if (current) modifiedFields.value.add(item.name)
    else modifiedFields.value.delete(item.name)
    return
  }

  if (current === original) {
    // Also consider source/fallback deltas in case this field is already dirty for other reasons.
    const originalSource = originalField.values?.source
    const currentSource = item.values?.source
    const originalFallback = originalField.values?.fallback?.[0]?.source
    const currentFallback = item.values?.fallback?.[0]?.source
    if (originalSource === currentSource && originalFallback === currentFallback) {
      modifiedFields.value.delete(item.name)
    }
  } else {
    modifiedFields.value.add(item.name)
  }
}

const handleSaveFieldMapping = async () => {
  if (isSaving.value) return
  isSaving.value = true

  try {
    // Keep request/change-summary tracking aligned with the actual edited rows. This also
    // covers input components that update v-model without reaching their explicit handler.
    for (const fieldName of locallyChangedFieldNames.value) {
      modifiedFields.value.add(fieldName)
    }

    const accountId = accountSettings.accountId

    const segmentId = activeSegment.value as FieldMappingSegment
    if (!isFieldMappingSegment(segmentId)) {
      throw new Error(`Unsupported field mapping segment: ${segmentId}`)
    }

    const activeFields: any[] = segmentConfig.value[segmentId] || []
    const allSegmentFields: any[] = segmentConfig.value.all || []
    const preservedDerived =
      segmentId === 'all'
        ? collectLegacyDerivedFeatureSelections(fieldMappingConfigs.value, 'all')
        : { selections: {}, defaultValues: {} }
    const selections: Record<string, string | string[]> =
      segmentId === 'all'
        ? { ...preservedDerived.selections }
        : buildSparseSegmentSelections(activeFields, allSegmentFields)
    const defaultValues: Record<string, unknown> =
      segmentId === 'all'
        ? { ...preservedDerived.defaultValues }
        : buildSparseSegmentDefaultValues(activeFields, allSegmentFields)

    if (segmentId === 'all') activeFields.forEach((field: any) => {
      if (isDerivedFeatureFieldName(field?.name)) return

      // Custom fields: ordered list of provider ids from customSources
      if (field.custom && Array.isArray(field.customSources)) {
        const ids = field.customSources.map((s: any) => s.id).filter(Boolean)
        if (ids.length === 1) {
          selections[field.name] = ids[0]
        } else if (ids.length > 1) {
          selections[field.name] = ids
        }

        // Default value: optional; only include when user provided a non-empty value
        const parsedDefault = parseDefaultValueForApi(field?.policy?.default_value)
        if (parsedDefault !== undefined) {
          defaultValues[field.name] = parsedDefault
        }
        return
      }

      // Normalized fields: preserve the complete ordered source chain.
      const primary = field.values?.source
      const fallbacks = Array.isArray(field.values?.fallback)
        ? field.values.fallback.map((entry: any) => entry?.source).filter(Boolean)
        : []

      if (!primary) {
        // No source selected — still capture the default value so that
        // default-only required fields (e.g. state="Ready for Sale") reach the backend
        // and are included in the published mapping.
        const parsedDefault = parseDefaultValueForApi(field?.policy?.default_value)
        if (parsedDefault !== undefined) {
          selections[field.name] = []
          defaultValues[field.name] = parsedDefault
        }
        return
      }

      const orderedSources = [primary, ...fallbacks].filter(
        (source, index, all) => source && all.indexOf(source) === index,
      )
      selections[field.name] =
        orderedSources.length === 1 ? orderedSources[0] : orderedSources

      // Default value: optional; only include when user provided a non-empty value
      const parsedDefault = parseDefaultValueForApi(field?.policy?.default_value)
      if (parsedDefault !== undefined) {
        defaultValues[field.name] = parsedDefault
      }
    })

    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const base = String(iseEndpoint).replace(/\/+$/g, '')
    // Always use PUT to /mapping-config/:jazelId (writes draft, even during onboarding).
    // Onboarding completion happens when user clicks "Finish" → publish-drafts.
    const path = `mapping-config/${encodeURIComponent(accountId)}`
    const url = `${base}/${path}`

    const token = accountSettings.sessionToken
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    }
    if (token) {
      headers.authorization = `Bearer ${token}`
    }

    const response = await fetch(url, {
      method: 'PUT',
      headers,
      body: JSON.stringify({
        jazelId: accountId,
        segmentId,
        expectedDraftRevision: fieldMappingDraftRevision.value,
        selections,
        ...(segmentId === 'all' || Object.keys(defaultValues).length > 0
          ? { defaultValues }
          : {}),
      }),
    })

    if (!response.ok) {
      const errorBody = await response.json().catch(() => null)
      if (response.status === 409) {
        fieldMappingDraftRevision.value =
          errorBody?.latestRevision != null ? String(errorBody.latestRevision) : null
        makeAlert(
          'Field mappings changed in another session. Your edits were kept; review them and save again.',
          'warning',
        )
        return
      }
      throw new Error(errorBody?.message || 'Failed to save field mapping')
    }
    const saved = await response.json().catch(() => null)
    if (saved?.data?.draftRevision != null) {
      fieldMappingDraftRevision.value = String(saved.data.draftRevision)
    }

    if (props.isOnBoarding) {
      emit('done')
      return
    }

    // Build detailed change summary
    const changes: any = {
      segment: activeSegment.value,
      timestamp: new Date().toISOString(),
      fieldsChanged: [],
    }

    // Track which fields were modified
    modifiedFields.value.forEach((fieldName) => {
      const currentField = segmentConfig.value[activeSegment.value]?.find(
        (f: any) => f.name === fieldName,
      )
      const originalField = originalSegmentConfig.value[activeSegment.value]?.find(
        (f: any) => f.name === fieldName,
      )

      if (currentField) {
        const change: any = { field: fieldName }

        // Track source changes
        if (originalField) {
          const oldSource = originalField.values?.source
          const newSource = currentField.values?.source
          if (oldSource !== newSource) {
            change.sourceChanged = { from: oldSource, to: newSource }
          }

          const oldFallback = originalField.values?.fallback?.[0]?.source
          const newFallback = currentField.values?.fallback?.[0]?.source
          if (oldFallback !== newFallback) {
            change.fallbackChanged = { from: oldFallback, to: newFallback }
          }
        } else {
          change.action = 'added'
          change.source = currentField.values?.source
        }

        changes.fieldsChanged.push(change)
      }
    })

    changes.totalModified = modifiedFields.value.size

    // Clear modified fields tracking on successful save
    modifiedFields.value.clear()
    originalSegmentConfig.value[segmentId] = JSON.parse(
      JSON.stringify(segmentConfig.value[segmentId] || []),
    )

    makeAlert('Field mappings saved as draft. Click "Publish Changes" to apply to live inventory.', 'success')

    // Notify Preview immediately, then reconcile against authoritative draft existence.
    // This correctly handles saves which remove the last patch as well as sibling drafts.
    await publishStore.markDirty('fieldMapping', changes)
    await publishStore.refreshUnpublishedChangesSummary()
    fieldMappingDraftPatches.value = await loadFieldMappingDraftPatches()
    refreshDraftModifiedFieldsForSegment(segmentId)
  } catch (error) {
    console.error('Error saving field mapping', error)
    makeAlert('Failed to save field mappings. Please try again.', 'error')
  } finally {
    isSaving.value = false
  }
}

/**
 * Updates the fallback source to the other available source if the current source is changed.
 */
const getUiSourceChain = (item: any): string[] => [
  item.values?.source || '',
  ...(Array.isArray(item.values?.fallback)
    ? item.values.fallback.map((entry: any) => entry?.source || '')
    : []),
].filter(Boolean)

const updateModifiedState = (item: any) => {
  const originalFields = originalSegmentConfig.value[activeSegment.value] || []
  const originalField = originalFields.find((field: any) => field.name === item.name)
  const unchanged =
    originalField &&
    JSON.stringify(getUiSourceChain(originalField)) === JSON.stringify(getUiSourceChain(item))
  if (unchanged) modifiedFields.value.delete(item.name)
  else modifiedFields.value.add(item.name)
}

const handleChangeSource = (item: any, selectedSource: string) => {
  if (!item.values) item.values = {}
  item.values.source = selectedSource
  const seen = new Set<string>(selectedSource ? [selectedSource] : [])
  item.values.fallback = (item.values?.fallback || []).filter((entry: any) => {
    const source = entry?.source || ''
    if (!source || seen.has(source)) return false
    seen.add(source)
    return true
  })
  markSegmentRowOrigin(item)
  updateModifiedState(item)
}

const handleChangeFallbackSource = (item: any, selectedSource: string, index = 0) => {
  if (!item.values) item.values = {}
  if (!Array.isArray(item.values.fallback)) item.values.fallback = []

  if (!selectedSource) {
    // Splicing compacts the ordered chain, promoting every later fallback.
    item.values.fallback.splice(index, 1)
    markSegmentRowOrigin(item)
    updateModifiedState(item)
    return
  }

  item.values.fallback = item.values.fallback.filter(
    (entry: any, entryIndex: number) =>
      entryIndex === index || entry?.source !== selectedSource,
  )
  if (index >= item.values.fallback.length) {
    item.values.fallback.push({ source: selectedSource })
  } else {
    item.values.fallback[index] = { source: selectedSource }
  }
  markSegmentRowOrigin(item)
  updateModifiedState(item)
}

const applyBulkPrimarySource = () => {
  const selectedSource = bulkPrimarySource.value
  if (!selectedSource) return

  const fields = segmentConfig.value[activeSegment.value] || []
  for (const field of fields) {
    if (!isBulkPrimaryApplicable(field, selectedSource)) continue
    if (!field.values) field.values = {}
    handleChangeSource(field, selectedSource)
  }
}

const applyBulkFallbackSource = () => {
  const selectedSource = bulkFallbackSource.value
  if (!selectedSource) return

  const fields = segmentConfig.value[activeSegment.value] || []
  for (const field of fields) {
    if (!isBulkFallbackApplicable(field, selectedSource)) continue
    if (!field.values) field.values = {}
    handleChangeFallbackSource(field, selectedSource, 0)
  }
}

// New config structure types
interface FieldMappingConfig {
  field: string
  sources: string[]
  required: boolean
  policy: { on_missing: string; default_value?: string }
}

interface SegmentFieldMappingConfig {
  segment_id: string
  field_mapping: {
    mappings: FieldMappingConfig[]
  }
}

const activeSegment = ref('')
const segmentConfig = ref<any>({}) // Will store transformed UI-friendly data
const originalSegmentConfig = ref<any>({}) // Store original state for dirty checking
const fieldMappingConfigs = ref<Record<string, any>>({})
const isTableLoading = ref(true)
const modifiedFieldsBySegment = ref<Record<string, Set<string>>>({})
const modifiedFields = computed(() => {
  const segmentId = activeSegment.value || 'all'
  if (!modifiedFieldsBySegment.value[segmentId]) {
    modifiedFieldsBySegment.value[segmentId] = new Set<string>()
  }
  return modifiedFieldsBySegment.value[segmentId]
})
const draftModifiedFields = ref<Set<string>>(new Set()) // Server-saved draft fields for display only
const fieldMappingDraftPatches = ref<FieldMappingDraftPatchesV3 | null>(null)
const fieldMappingDraftRevision = ref<string | null>(null)

// Filter options
const filterOptions = ref({
  showModifiedOnly: false,
  showCustomOnly: false,
  showInvalidSourcesOnly: false,
  selectedSources: [] as string[],
})

// Fetch available fields from ISE mapping-config/fields endpoint
// Returns array of { key, sources, starterDefaultValue? } — the editable field catalog
const fetchAvailableFields = async (): Promise<FieldMappingCatalogField[]> => {
  try {
    const iseEndpoint = import.meta.env.VITE_ISE_BACKEND_URL
    const response = await fetchIseMappingFields(iseEndpoint, accountSettings.accountId)

    // The API returns { success, data: [{ key, sources }], feedDetails, ... }
    const fields = Array.isArray(response?.data) ? response.data : []

    // Cache feedDetails as available sources if provided
    const feedDetails = (response as any)?.feedDetails || (response?.data as any)?.feedDetails
    if (feedDetails && Array.isArray(feedDetails)) {
      sourcesStore.setSources(feedDetails)
    }

    if (fields.length > 0) {
      return fields
    }

    // If no fields returned, use default
    throw new Error('No fields returned from ISE')
  } catch (error) {
    console.error('Failed to fetch available fields from ISE', error)
    // Return default fields if fetch fails (with empty sources arrays)
    return [
      'year',
      'make',
      'model',
      'trim',
      'vin',
      'stock_number',
      'exterior_color',
      'interior_color',
      'mileage',
      'display_price',
      'condition',
      'body_style',
      'transmission',
      'drivetrain',
      'engine',
      'fuel_type',
      'doors',
      'passengers',
      'city_mpg',
      'highway_mpg',
      'description',
      'features',
      'options',
      'images',
      'date_in_stock',
    ].map((key) => ({ key, sources: [] }))
  }
}

// Build the first-run config from backend-owned source recommendations.
const buildDefaultConfig = (
  fields: FieldMappingCatalogField[],
): Record<string, any> => {
  return {
    mapping_config: {
      all: {
        field_mapping: {
          mappings: buildRecommendedCatalogMappings(fields),
        },
      },
    },
  }
}

// Transform new config to UI-friendly format
const transformConfigForUI = (config: Record<string, any>) => {
  const uiData: any = {}

  // Access segments from mapping_config
  const segments = config.mapping_config || {}
  for (const [segmentId, segmentData] of Object.entries(segments)) {
    const typedSegmentData = segmentData as SegmentFieldMapping | undefined
    if (!typedSegmentData?.field_mapping?.mappings) continue

    uiData[segmentId] = typedSegmentData.field_mapping.mappings.map((mapping: any) => {
      // Handle custom fields
      if (mapping.custom) {
        // Keep source IDs as-is (backend now returns and accepts with import_ prefix)
        const rawSources = Array.isArray(mapping.sources) ? mapping.sources : []
        const normalizedSources = rawSources.map((s: any) => {
          return {
            id: s?.id || '',
            field: s?.field || '',
            path: s?.path || null,
          }
        })

        return {
          name: mapping.field,
          custom: true,
          customLabel: mapping.label,
          customType: mapping.type || 'string',
          customSources: normalizedSources,
          customDefault: mapping.default,
          // For UI, keep a simple list of { id, name } for potential future use
          sources: normalizedSources.map((s: any) => {
            const provider = availableSources.value.find((src) => src.id === s.id)
            return {
              id: s.id,
              name: provider?.friendlyName || s.id,
            }
          }),
          values: {
            source: normalizedSources[0]?.id || '',
            fallback: normalizedSources.slice(1).map((s: any) => ({ source: s.id })),
          },
          required: mapping.required || false,
          policy: (() => {
            const p = mapping.policy || { on_missing: 'fallback' }
            const dv = mapping.default_value ?? p.default_value
            if (dv !== undefined) {
              p.default_value = formatDefaultValueForInput(dv)
            }
            return p
          })(),
          indexing_hint: mapping.indexing_hint || {
            searchable: false,
            facetable: false,
            sortable: false,
          },
        }
      }

      // Handle normalized fields
      const sources = Array.isArray(mapping.sources) ? mapping.sources : []
      const primarySource = sources[0] || ''
      return {
        name: mapping.field,
        // Keep sources as simple strings - display them as-is from the API
        sources: sources,
        values: {
          source: primarySource,
          // The table displays two fallback controls but retains any longer
          // configured tail so loading and saving never truncates it.
          fallback: sources.slice(1).map((source: string) => ({ source })),
        },
        required: mapping.required || false,
        policy: (() => {
          const p = mapping.policy || { on_missing: 'fallback' }
          const dv = mapping.default_value ?? p.default_value
          if (dv !== undefined) {
            p.default_value = formatDefaultValueForInput(dv)
          }
          return p
        })(),
      }
    })
  }

  return uiData
}

const loadFieldMappingDraftPatches = async (): Promise<FieldMappingDraftPatchesV3 | null> => {
  try {
    const draft = await fetchFieldMappingDraft({
      iseEndpoint: import.meta.env.VITE_ISE_BACKEND_URL,
      jazelId: accountSettings.accountId,
      token: accountSettings.sessionToken,
    })
    const revision = (draft as any)?.revision ?? (draft as any)?.draftRevision
    fieldMappingDraftRevision.value = revision != null ? String(revision) : null
    if ((draft as any)?.version !== 3 || !(draft as any)?.patches) return null
    return (draft as any).patches as FieldMappingDraftPatchesV3
  } catch (error) {
    console.warn('VehicleFieldMapping.loadDraftFailed', error)
    return null
  }
}

const keepAvailableFieldsOnly = (
  uiConfig: Record<string, any>,
  availableFields: FieldMappingCatalogField[],
) => {
  for (const segmentId in uiConfig) {
    const fields = uiConfig[segmentId]
    if (!Array.isArray(fields)) continue
    uiConfig[segmentId] = fields.filter((field: any) => {
      if (field.custom) return true
      if (isDerivedFeatureFieldName(field.name)) return false
      return availableFields.some((availableField) => availableField.key === field.name)
    })
  }
}

const mergeAvailableSourcesIntoUiConfig = (
  uiConfig: Record<string, any>,
  availableFields: FieldMappingCatalogField[],
) => {
  for (const segmentId in uiConfig) {
    const fields = uiConfig[segmentId]
    if (!Array.isArray(fields)) continue
    fields.forEach((field: any) => {
      const availableField = availableFields.find((item) => item.key === field.name)
      if (availableField && !field.custom) {
        field.sources = availableField.sources || []
      }
    })
  }
}

const refreshDraftModifiedFieldsForSegment = (segmentId: string) => {
  draftModifiedFields.value = new Set(
    collectDraftFieldMappingFieldsForSegment(fieldMappingDraftPatches.value, segmentId),
  )
}

const markSegmentRowOrigin = (item: any) => {
  if (activeSegment.value === 'all') return
  const allRow = (segmentConfig.value.all || []).find((row: any) => row.name === item.name)
  const sparse = buildSparseSegmentSelections([item], allRow ? [allRow] : [])
  const sparseDefaults = buildSparseSegmentDefaultValues([item], allRow ? [allRow] : [])
  item.origin =
    Object.prototype.hasOwnProperty.call(sparse, item.name) ||
    Object.prototype.hasOwnProperty.call(sparseDefaults, item.name)
      ? 'override'
      : 'inherited'
  item.defaultOrigin = Object.prototype.hasOwnProperty.call(sparseDefaults, item.name)
    ? 'override'
    : 'inherited'
}

const resetToAllVehicles = (item: any) => {
  const allRow = (segmentConfig.value.all || []).find((row: any) => row.name === item.name)
  if (!allRow) return
  resetSegmentRowToAll(item, allRow)
  const original = (originalSegmentConfig.value[activeSegment.value] || []).find(
    (row: any) => row.name === item.name,
  )
  if (compareFieldMappingLocalDirty(item, original) === 'clean') {
    modifiedFields.value.delete(item.name)
  } else {
    modifiedFields.value.add(item.name)
  }
}

const initialize = async (segment: string) => {
  isTableLoading.value = true

  // 1) Fetch available dealer sources from JAMS ISE backend
  // Available dealer sources are now fetched from cache (loaded on app start)
  // No need to fetch here - using sourcesStore.sources via computed property

  // 2) Build the selector from effective segmentation, including staged segment changes.
  try {
    const response = await fetchSegmentationConfig(
      import.meta.env.VITE_ISE_BACKEND_URL,
      accountSettings.accountId,
      { includeDraft: true },
    )
    const segmentsConfig = response?.data?.segments

    if (segmentsConfig && typeof segmentsConfig === 'object' && Object.keys(segmentsConfig).length > 0) {
      segmentationOptions.value = Object.entries(segmentsConfig)
        .filter(([id]) => isFieldMappingSegment(id))
        .sort(([leftId, left]: [string, any], [rightId, right]: [string, any]) => {
          if (leftId === 'all') return -1
          if (rightId === 'all') return 1
          return (left?.priority ?? Number.MAX_SAFE_INTEGER) -
            (right?.priority ?? Number.MAX_SAFE_INTEGER)
        })
        .map(([id, segment]: [string, any]) => ({
          text: segment?.name || id,
          value: id,
        }))
    } else {
      segmentationOptions.value = [{ text: 'All Vehicles', value: 'all' }]
    }
  } catch (error) {
    console.error('Error loading segments:', error)
    // Fallback to minimal segments (just "all")
    segmentationOptions.value = [
      { text: 'All Vehicles', value: 'all' }
    ]
  }

  // 3) Fetch available fields from ISE mapping-config/fields
  const availableFields = await fetchAvailableFields()

  // 4) Get current mapping config from JAMS ise_srp_import_mapping product
  let currentConfig: Record<string, any> = {}
  try {
    const endpoint = appConfig.jamsBackendUrl
    const accountId = accountSettings.accountId
    const token = accountSettings.sessionToken
    const url = `${endpoint}/account/products/${accountId}/ise_srp_import_mapping`
    const res = await fetch(url, { headers: { authorization: `Bearer ${token}` } })
    const json: any = await res.json()

    // Some environments return the config at the top-level (no `config` key)
    const cfg = json?.config && typeof json.config === 'object' ? json.config : json

    // Check if cfg has mapping_config with segment keys ('all' at minimum)
    if (cfg && typeof cfg === 'object' && cfg.mapping_config?.all?.field_mapping?.mappings) {
      currentConfig = cfg
    } else {
      // Use default config only when no mapping exists yet (UI-only defaults)
      currentConfig = buildDefaultConfig(availableFields)
    }
  } catch (error) {
    console.error('Failed to fetch mapping config', error)
    currentConfig = buildDefaultConfig(availableFields)
  }

  if (!currentConfig.mapping_config?.cpo && currentConfig.mapping_config?.certified) {
    currentConfig = JSON.parse(JSON.stringify(currentConfig))
    currentConfig.mapping_config.cpo = currentConfig.mapping_config.certified
  }

  fieldMappingDraftPatches.value = await loadFieldMappingDraftPatches()
  const effectiveConfig = applyFieldMappingDraftOverlay(currentConfig, fieldMappingDraftPatches.value)

  fieldMappingConfigs.value = effectiveConfig
  const publishedUiConfig = transformConfigForUI(currentConfig)
  const effectiveUiConfig = transformConfigForUI(effectiveConfig)

  // Filter out fields that are no longer available (not in availableFields from API)
  // This removes legacy/deprecated fields from saved configs while keeping custom fields.
  keepAvailableFieldsOnly(publishedUiConfig, availableFields)
  keepAvailableFieldsOnly(effectiveUiConfig, availableFields)

  // The config only has the configured sources, but dropdowns need all available sources.
  mergeAvailableSourcesIntoUiConfig(publishedUiConfig, availableFields)
  mergeAvailableSourcesIntoUiConfig(effectiveUiConfig, availableFields)

  // Show every editable field, not just those already saved. Feed availability must not
  // hide a field (e.g. `state` on ICC accounts); missing catalog fields are injected as
  // empty rows so a dealer can select a source or set a default. Injecting into both the
  // effective and published views keeps untouched rows from registering as changes.
  ensureCatalogFieldsPresent(effectiveUiConfig, availableFields, 'all')
  ensureCatalogFieldsPresent(publishedUiConfig, availableFields, 'all')

  for (const option of segmentationOptions.value) {
    const segmentId = String(option.value)
    if (segmentId === 'all' || !isFieldMappingSegment(segmentId)) continue
    effectiveUiConfig[segmentId] = buildEffectiveSegmentRows(
      effectiveUiConfig.all || [],
      effectiveUiConfig[segmentId] || [],
    )
    publishedUiConfig[segmentId] = buildEffectiveSegmentRows(
      publishedUiConfig.all || [],
      publishedUiConfig[segmentId] || [],
    )
  }

  segmentConfig.value = effectiveUiConfig
  // Local dirty state starts from the draft-effective rows, while Draft chips separately
  // identify server-saved changes.
  originalSegmentConfig.value = JSON.parse(JSON.stringify(effectiveUiConfig))
  refreshDraftModifiedFieldsForSegment(segment)

  modifiedFieldsBySegment.value = {}

  isTableLoading.value = false
}

onMounted(async () => {
  activeSegment.value = 'all' // Default to 'all' segment
  await initialize(activeSegment.value)
})

watch(
  () => activeSegment.value,
  (selectedSegment) => {
    // Clear filters when switching segments (except source filters, keep those)
    filterOptions.value.showModifiedOnly = false
    filterOptions.value.showCustomOnly = false
    filterOptions.value.showInvalidSourcesOnly = false
    if (selectedSegment !== 'all' && isFieldMappingSegment(selectedSegment)) {
      const localOverrides = (segmentConfig.value[selectedSegment] || []).filter(
        (row: any) => row.origin === 'override',
      )
      segmentConfig.value[selectedSegment] = buildEffectiveSegmentRows(
        segmentConfig.value.all || [],
        localOverrides,
      )
    }
    refreshDraftModifiedFieldsForSegment(selectedSegment)
  },
)
</script>

<style scoped>
/* Floating Save button for mobile */
.save-fab {
  position: fixed;
  right: 16px;
  bottom: 16px;
  z-index: 2000;
}

/* Table header styling */
.v-data-table th {
  font-weight: bold;
  background-color: #f5f5f5;
}

/* Alternating row backgrounds for better readability */
.field-mapping-table :deep(tbody tr:nth-child(even)) {
  background-color: rgba(0, 0, 0, 0.02);
}

/* Column dividers for better scanning */
.field-mapping-table :deep(td) {
  border-right: 1px solid rgba(0, 0, 0, 0.06);
}

.field-mapping-table :deep(td:last-child) {
  border-right: none;
}

/* Visual indicator for custom field rows */
.custom-field-row {
  border-left: 3px solid #2196f3 !important;
}

/* Group header rows: remove vertical borders across all columns */
.field-mapping-table :deep(tr.group-header-row td),
.field-mapping-table :deep(tr:has(.field-group-header) td) {
  background-color: #FFF !important;
  border-left: none !important;
  border-right: none !important;
}

/* Highlight for saved draft and local unsaved rows */
.field-mapping-table :deep(tr.draft-field-row td) {
  background-color: rgba(var(--v-theme-warning), 0.1) !important;
}

.field-mapping-table :deep(tr.draft-field-row:hover td) {
  background-color: rgba(var(--v-theme-warning), 0.14) !important;
}

.field-mapping-table :deep(tr.modified-field-row td) {
  background-color: rgba(var(--v-theme-warning), 0.16) !important;
}

.field-mapping-table :deep(tr.modified-field-row:hover td) {
  background-color: rgba(var(--v-theme-warning), 0.2) !important;
}

/* Clickable custom field labels */
.custom-field-clickable {
  cursor: pointer;
}

.custom-field-clickable:focus-visible {
  outline: 2px solid #1976d2;
  outline-offset: 2px;
}

.custom-field-edit-icon {
  color: #757575;
}

/* Monospaced font for raw field names */
.field-name-code {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
  background-color: rgba(0, 0, 0, 0.05);
  padding: 2px 6px;
  border-radius: 3px;
}

.field-name-code-small {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.75rem;
  background-color: rgba(0, 0, 0, 0.04);
  padding: 1px 4px;
  border-radius: 2px;
}

/* Group header styling inside the table */
.field-group-header {
  margin-top: 16px;
  padding-top: 8px;
  padding-bottom: 6px;
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: rgba(0, 0, 0, 0.65);
  border-top: none;
  background-color: transparent;
}

/* Indent child rows visually under their group header */
.field-row.field-row-indented {
  padding-left: 12px;
}

.field-mapping-bulk-actions__ops {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 2rem;
  row-gap: 0.75rem;
}

.field-mapping-bulk-actions__group {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  max-width: 100%;
  /* Prefer width for [select ~280px][Apply]; shrinks below this on narrow rows. */
  flex: 1 1 30rem;
}

.field-mapping-bulk-actions__group :deep(.v-select) {
  flex: 1 1 12rem;
  min-width: 0;
  max-width: min(280px, 100%);
}

.field-mapping-bulk-actions__group :deep(.v-btn) {
  flex: 0 0 auto;
}

/* Filter chips */
.filter-chip {
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Delete button - neutral by default, red on hover (destructive action) */
.delete-btn {
  color: rgba(0, 0, 0, 0.6);
  transition: color 0.2s ease;
}

.delete-btn:hover {
  color: #d32f2f !important;
}
</style>
